import React, { useMemo } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { palette } from '../../theme';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AXIS_COLOR = 'rgba(148,163,184,0.75)';
const GRID_COLOR = 'rgba(56,189,248,0.08)';

const baseTooltip = {
  backgroundColor: 'rgba(5,8,22,0.94)',
  borderColor: 'rgba(56,189,248,0.28)',
  borderWidth: 1,
  titleColor: '#F8FAFC',
  bodyColor: '#94A3B8',
  padding: 12,
  cornerRadius: 10,
  displayColors: true,
  boxWidth: 8,
  boxHeight: 8,
  usePointStyle: true,
  titleFont: { family: 'Outfit', size: 12, weight: '600' },
  bodyFont: { family: 'JetBrains Mono', size: 11 },
};

const baseScales = {
  x: {
    grid: { color: GRID_COLOR, drawTicks: false },
    border: { display: false },
    ticks: { color: AXIS_COLOR, font: { family: 'JetBrains Mono', size: 10 }, maxRotation: 0, autoSkipPadding: 18 },
  },
  y: {
    grid: { color: GRID_COLOR, drawTicks: false },
    border: { display: false },
    ticks: { color: AXIS_COLOR, font: { family: 'JetBrains Mono', size: 10 }, padding: 8 },
    beginAtZero: false,
  },
};

/**
 * Holographic line chart with a soft glow gradient and interactive points.
 */
export function HoloLineChart({
  labels = [],
  datasets = [],
  height = 260,
  yTitle,
  legend = false,
  filled = true,
  beginAtZero = false,
  options = {},
}) {
  const data = useMemo(
    () => ({
      labels,
      datasets: datasets.map((dataset, index) => {
        const color = dataset.color || [palette.primary, palette.secondary, palette.success][index % 3];
        return {
          label: dataset.label,
          data: dataset.data,
          borderColor: color,
          borderWidth: 2,
          tension: 0.42,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: '#050816',
          pointHoverBorderWidth: 2,
          pointBackgroundColor: color,
          fill: filled && datasets.length === 1,
          backgroundColor: (context) => {
            const { chart } = context;
            if (!chart.chartArea) return alpha(color, 0.12);
            const gradient = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom);
            gradient.addColorStop(0, alpha(color, 0.34));
            gradient.addColorStop(0.55, alpha(color, 0.08));
            gradient.addColorStop(1, alpha(color, 0));
            return gradient;
          },
          ...dataset,
        };
      }),
    }),
    [labels, datasets, filled]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 750, easing: 'easeOutQuart' },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: legend
          ? {
              display: true,
              position: 'top',
              align: 'end',
              labels: {
                color: AXIS_COLOR,
                usePointStyle: true,
                pointStyle: 'circle',
                boxWidth: 7,
                boxHeight: 7,
                padding: 14,
                font: { family: 'Outfit', size: 11 },
              },
            }
          : { display: false },
        tooltip: {
          ...baseTooltip,
          callbacks: {
            label: (context) => ` ${context.dataset.label}: ${context.parsed.y}`,
          },
        },
      },
      scales: {
        ...baseScales,
        y: { ...baseScales.y, beginAtZero, title: yTitle ? { display: true, text: yTitle, color: AXIS_COLOR, font: { size: 10 } } : undefined },
      },
      ...options,
    }),
    [legend, yTitle, beginAtZero, options]
  );

  return (
    <Box sx={{ height, position: 'relative' }}>
      <Line data={data} options={chartOptions} />
    </Box>
  );
}

/**
 * Holographic bar chart with depth illusion via layered gradient bars.
 */
export function HoloBarChart({
  labels = [],
  datasets = [],
  height = 260,
  yTitle,
  legend = false,
  options = {},
}) {
  const data = useMemo(
    () => ({
      labels,
      datasets: datasets.map((dataset, index) => {
        const color = dataset.color || [palette.primary, palette.secondary, palette.success, palette.warning, palette.danger][index % 5];
        return {
          label: dataset.label,
          data: dataset.data,
          backgroundColor: (context) => {
            const { chart } = context;
            if (!chart.chartArea) return alpha(color, 0.7);
            const gradient = chart.ctx.createLinearGradient(0, chart.chartArea.bottom, 0, chart.chartArea.top);
            gradient.addColorStop(0, alpha(color, 0.28));
            gradient.addColorStop(1, alpha(color, 0.95));
            return gradient;
          },
          hoverBackgroundColor: color,
          borderRadius: 8,
          borderSkipped: false,
          barPercentage: 0.62,
          categoryPercentage: 0.72,
          ...dataset,
        };
      }),
    }),
    [labels, datasets]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 750, easing: 'easeOutQuart' },
      plugins: {
        legend: legend
          ? {
              display: true,
              position: 'top',
              align: 'end',
              labels: {
                color: AXIS_COLOR,
                usePointStyle: true,
                pointStyle: 'circle',
                boxWidth: 7,
                boxHeight: 7,
                padding: 14,
                font: { family: 'Outfit', size: 11 },
              },
            }
          : { display: false },
        tooltip: {
          ...baseTooltip,
          callbacks: {
            label: (context) => ` ${context.dataset.label}: ${context.parsed.y}`,
          },
        },
      },
      scales: {
        ...baseScales,
        y: { ...baseScales.y, beginAtZero: true, title: yTitle ? { display: true, text: yTitle, color: AXIS_COLOR, font: { size: 10 } } : undefined },
      },
      ...options,
    }),
    [legend, yTitle, options]
  );

  return (
    <Box sx={{ height, position: 'relative' }}>
      <Bar data={data} options={chartOptions} />
    </Box>
  );
}

/** Compact chart frame with title and optional trailing controls. */
export function ChartFrame({ title, subtitle, action, children, height }) {
  return (
    <Box>
      {(title || action) && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box>
            {title && (
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
            )}
            {subtitle && (
              <Typography sx={{ fontSize: '0.73rem', color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>
            )}
          </Box>
          {action}
        </Box>
      )}
      <Box sx={{ height }}>{children}</Box>
    </Box>
  );
}
